/**
 * SAAS-USD-BILLING-1 · Phase 0.5 — Required onboarding gate.
 *
 * Wraps billing-sensitive merchant surfaces. If `merchant_type` is
 * `UNCLASSIFIED`, shows a required prompt to classify BEFORE rendering
 * children. Once classified (or if merchant is on a legacy type that is
 * locked), the wrapped children render normally.
 *
 * NB: This gate deliberately does not enforce any billing decisions
 * itself — those live entirely on the backend. It only nudges merchants
 * into an authoritative merchant_type before they touch billing UI.
 */
import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import api from "@/lib/api";
import BusinessTypeSelector from "./BusinessTypeSelector";

export default function BusinessTypeRequiredGate({ children }) {
  const [store, setStore] = useState(null);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/merchant/store")
      .then((r) => setStore(r.data))
      .catch(() => setStore(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  const type = store?.merchant_type || "UNCLASSIFIED";
  if (!store || type !== "UNCLASSIFIED") return <>{children}</>;

  return (
    <div data-testid="business-type-required-gate">
      <div className="pf-card mb-6 border-amber-200/80 bg-amber-50/60">
        <div className="p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-4 items-center">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">
              <AlertCircle className="w-4 h-4" /> One quick step
            </div>
            <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-slate-900 mt-2">
              Set your Business Type before continuing
            </h3>
            <p className="text-xs md:text-sm text-slate-600 mt-2 max-w-xl leading-relaxed">
              Select your business type so Cyannect can configure the correct campaign and billing
              options for your business. This is required before touching billing.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="pf-btn-primary px-4 py-2.5 rounded-full text-sm font-semibold"
            data-testid="business-type-required-cta"
          >
            Choose Business Type
          </button>
        </div>
      </div>
      <BusinessTypeSelector
        open={open}
        onClose={() => setOpen(false)}
        currentValue={type}
        locked={false}
        onSaved={(next) =>
          setStore((s) => (s ? { ...s, merchant_type: next } : s))
        }
      />
    </div>
  );
}
